/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useState, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  BuildingType,
  NewsItem,
  Scenario,
  ScenarioObjective,
  QuizQuestion,
  Grid,
  CityStats,
  ResearchState,
  GameStatus,
  Milestone,
} from '../types';
import {
  GRID_SIZE,
  SCENARIOS,
  TUTORIAL_CAMPAIGN,
  SCENARIO_ID_MAP,
  TECH_TREE,
  DEFAULT_UNLOCKED,
} from '../constants';
import { resolvePlayAudio } from '../src/adapters/ui/hooks/useAudio';
import { playUiSfx } from '../utils/uiAudio';
import { gameStore } from '../stores/gameStore';
import { simulationEngine } from '../src/domain/services/SimulationEngine';
import {
  createPlatformSession,
  getOrCreatePlayerRef,
} from '../src/adapters/ui/hooks/usePlatformPersistence';
import type { ActiveModal } from '../components/UIOverlay';
import { TRAINING_PROGRESS_KEY } from '../utils/playerAnalytics';
import { createFreshMilestones } from '../utils/milestones';

export interface UseScenarioFlowDeps {
  addNewsItem: (item: NewsItem) => void;
  setNewsFeed: Dispatch<SetStateAction<NewsItem[]>>;
  setMilestones: (milestones: Milestone[] | ((prev: Milestone[]) => Milestone[])) => void;
  setGameStatus: (status: GameStatus) => void;
  setGameStarted: (value: boolean) => void;
  setStats: (stats: CityStats | ((prev: CityStats) => CityStats)) => void;
  setResearch: (research: ResearchState | ((prev: ResearchState) => ResearchState)) => void;
  setGrid: (grid: Grid | ((prev: Grid) => Grid)) => void;
  setActiveModal: (modal: ActiveModal) => void;
  gameStatus: GameStatus;
}

export function useScenarioFlow({
  addNewsItem,
  setNewsFeed,
  setMilestones,
  setGameStatus,
  setGameStarted,
  setStats,
  setResearch,
  setGrid,
  setActiveModal,
  gameStatus,
}: UseScenarioFlowDeps) {
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);
  const [scenarioObjectives, setScenarioObjectives] = useState<ScenarioObjective[]>([]);
  const [activeQuiz, setActiveQuiz] = useState<QuizQuestion | null>(null);

  const loadScenario = useCallback(
    (
      scenarioId: string,
      triggerStory: (customText?: string) => Promise<void>
    ) => {
      const scenario =
        SCENARIOS.find((s) => s.id === scenarioId) ||
        TUTORIAL_CAMPAIGN.find((s) => s.id === scenarioId);
      if (!scenario) return;

      setActiveScenario(scenario);
      setGameStatus('playing');
      setGameStarted(true);
      gameStore.getState().setTickNumber(0);
      setMilestones(createFreshMilestones());
      setNewsFeed([]);
      resolvePlayAudio({ type: 'start_ambient' });

      setStats({
        budget: scenario.startingState.budget,
        compliance: scenario.startingState.comp,
        threat: 0,
        threatHistory: Array(20).fill(0),
        loanCount: scenario.startingState.debt,
        day: 1,
        lastPitchDay: -10,
        activeProducts: scenario.startingState.products,
      });

      let unlocked = [...DEFAULT_UNLOCKED];
      let maxLevel = 1;
      const scenarioTechs = scenario.unlockedTechs || [];

      TECH_TREE.forEach((node) => {
        if (scenarioTechs.includes(node.id)) {
          unlocked = [...unlocked, ...node.unlocks];
          if (node.effects?.maxLevelIncrease) maxLevel += node.effects.maxLevelIncrease;
        }
      });

      setResearch({
        unlockedBuildings: unlocked,
        completedTechs: scenarioTechs,
        activeResearchId: null,
        researchProgress: 0,
        currentMaxLevel: maxLevel,
      });

      const newGrid = simulationEngine.createInitialGrid().map((row) =>
        row.map((tile) => ({
          ...tile,
          buildingType: BuildingType.None,
          isConnected: false,
          level: 1,
        }))
      );

      const offset = Math.floor((GRID_SIZE - 5) / 2);

      scenario.startingState.grid.forEach((row, r) => {
        row.forEach((cellVal, c) => {
          if (cellVal > 0) {
            const type = SCENARIO_ID_MAP[cellVal];
            if (type) {
              newGrid[r + offset][c + offset] = {
                x: c + offset,
                y: r + offset,
                buildingType: type,
                level: 1,
                isConnected: false,
              };
            }
          }
        });
      });
      setGrid(newGrid);

      const objs = scenario.objectives.map((desc) => {
        return {
          description: desc,
          targetType: 'stat_reach' as const,
          targetValue: 1,
          completed: false,
        };
      });
      setScenarioObjectives(objs);

      setActiveModal('scenario_briefing');
      triggerStory(scenario.voicePrompt);
      void createPlatformSession(scenarioId, getOrCreatePlayerRef());
    },
    [
      setActiveModal,
      setGameStarted,
      setGameStatus,
      setGrid,
      setMilestones,
      setNewsFeed,
      setResearch,
      setStats,
    ]
  );

  useEffect(() => {
    if (activeScenario && scenarioObjectives.length > 0 && gameStatus === 'playing') {
      const allCompleted = scenarioObjectives.every((obj) => obj.completed);
      if (allCompleted) {
        setGameStatus('scenario_won');
        addNewsItem({
          id: Date.now().toString(),
          text: 'MISSION ACCOMPLISHED. SYSTEM STABLE.',
          type: 'positive',
        });
        playUiSfx('success');

        const tutorialIndex = TUTORIAL_CAMPAIGN.findIndex((t) => t.id === activeScenario.id);
        if (tutorialIndex !== -1) {
          const currentProgress = parseInt(localStorage.getItem(TRAINING_PROGRESS_KEY) || '0');
          if (tutorialIndex >= currentProgress) {
            const newProgress = tutorialIndex + 1;
            localStorage.setItem(TRAINING_PROGRESS_KEY, newProgress.toString());
            addNewsItem({
              id: Date.now().toString(),
              text: 'ACADEMY MODULE UNLOCKED.',
              type: 'positive',
            });
          }
        }
      }
    }
  }, [scenarioObjectives, activeScenario, gameStatus, addNewsItem, setGameStatus]);

  return {
    activeScenario,
    setActiveScenario,
    scenarioObjectives,
    setScenarioObjectives,
    activeQuiz,
    setActiveQuiz,
    loadScenario,
  };
}